import { createNanoEvents } from 'nanoevents';
import { parseJson, getRequestId } from './utils';

interface MessageReq {
    type: 'request';
    /** 不需回复标记 */
    noResp?: boolean;
    requestId: string;
    method: string;
    params?: any[];
}

interface MessageResp {
    type: 'response';
    requestId: string;
    result?: any;
}

type Message = MessageReq | MessageResp;

// 以 `namespace` 为事件名
interface Events {
    [namespace: string]: (msg: Message, source: WindowProxy) => void;
}

/**
 * ```
 * type F1 = () => void;
 * type P1 = Promisify<F1>; // () => void
 * type F2 = () => number;
 * type P2 = Promisify<F2>; // () => Promise<number>
 * ```
 */
type Promisify<F extends (...args: any) => any> = ReturnType<F> extends void
    ? (...args: Parameters<F>) => void
    : (...args: Parameters<F>) => Promise<ReturnType<F>>;

type ServiceProxy<S extends Service, K extends keyof S> = {
    [x in K]: S[x] extends (...args: any) => any ? Promisify<S[x]> : never;
} & {
    /** 析构函数，注销事件监听；通过 Proxy 注入； */
    destory(): void;
};

/** 服务基类 */
export class Service {
    public static namespace = 'tdocs';

    /** 标记无需返回的协议 */
    public static voidMethods: string[] = [];
}

/** 基于 postMessage 的多窗口通信协议 */
export class MessageProtocol {
    private static instance: MessageProtocol;

    public static getInstance(): MessageProtocol {
        if (!MessageProtocol.instance) {
            MessageProtocol.instance = new MessageProtocol();
        }
        return MessageProtocol.instance;
    }

    private emitter = createNanoEvents<Events>();

    private constructor() {
        window.addEventListener('message', this.handleMessageEvent);
    }

    /**
     * [服务方] 注册服务
     * @returns deregister 方法
     */
    public registerService<C extends typeof Service>(cls: C): () => void {
        const instance = new cls();

        return this.emitter.on(cls.namespace, async (msg, source) => {
            if (msg.type !== 'request') return;

            const result = await (instance[msg.method] as Function)(...(msg.params ?? []));
            if (msg.noResp) return;

            const response: MessageResp = {
                type: 'response',
                requestId: msg.requestId,
                result,
            };

            postMessage(source, cls.namespace, response);
        });
    }

    /**
     * [调用方] 创建服务代理对象
     * @param targetWindow 目标窗口，即 Service 提供方
     * @param cls Service 类
     */
    public createServiceProxy<C extends typeof Service, I extends InstanceType<C>>(
        targetWindow: Window,
        cls: C,
    ): ServiceProxy<I, keyof I> {
        const instance = new cls();
        const callbackMap = new Map<string, Function>();
        let seq = 0;

        // 注册 response 回调, 以 namespace 为事件名；
        const off = this.emitter.on(cls.namespace, async (msg) => {
            if (msg.type !== 'response') return;

            callbackMap.get(msg.requestId)?.(msg.result);
            callbackMap.delete(msg.requestId);
        });

        const handler: ProxyHandler<Service> = {
            get(target, props: string) {
                // 析构函数只需注销事件监听
                if (props === 'destory') return off;

                if (target[props] instanceof Function) {
                    return function (...args) {
                        seq += 1;
                        const requestId = getRequestId(seq);
                        const request: MessageReq = {
                            type: 'request',
                            requestId,
                            method: props,
                            params: args,
                        };

                        if (cls.voidMethods.includes(props)) {
                            request.noResp = true;
                            postMessage(targetWindow, cls.namespace, request);
                            return;
                        }

                        postMessage(targetWindow, cls.namespace, request);

                        return new Promise((resolve) => {
                            callbackMap.set(requestId, resolve);
                        });
                    };
                }

                return;
            },
        };

        return new Proxy(instance, handler) as any;
    }

    /**
     * 将全局的 message 转化为私有 eventBus 的事件，以 `namespace` 为事件名
     */
    private handleMessageEvent = ({ data, origin, source }: MessageEvent) => {
        if (origin !== location.origin) return;

        if (typeof data !== 'string') return;

        // 已注册的事件名都是 namespace
        const namespaceList = Object.keys(this.emitter.events);
        // eslint-disable-next-line no-restricted-syntax
        for (const namespace of namespaceList) {
            if (!data.startsWith(namespace)) continue;

            const msg = parseJson(data.slice(namespace.length));
            if (!msg) continue;

            // 检查消息体，无论通过与否都不再继续匹配
            if (isMessageReq(msg) || isMessageResp(msg)) {
                this.emitter.emit(namespace, msg, source as WindowProxy);
            }
            return;
        }
    };
}

function postMessage(targetWindow: Window, namespace: string, data: Message) {
    const text = `${namespace}${JSON.stringify(data)}`;
    targetWindow.postMessage(text, '*');
}

function isMessageReq(msg: any): msg is MessageReq {
    return msg && msg.type === 'request' && typeof msg.method === 'string';
}

function isMessageResp(msg: any): msg is MessageResp {
    return msg && msg.type === 'response' && typeof msg.requestId === 'string';
}
